import { type JwtPayload, jwtDecode } from 'jwt-decode';

type GetAccessTokenSilently = (options?: {
  cacheMode?: 'on' | 'off' | 'cache-only';
}) => Promise<string>;

/**
 * Auth0 only treats a token as expired 60s early, which is fine for a request but not for a
 * stream that stays open for hours: a reconnect handed a nearly-expired token is a 401 waiting
 * to happen. Reconnects near the boundary get a new token up front instead.
 */
export const SSE_MIN_TOKEN_REMAINING_MS = 5 * 60_000;

const msUntilExpiry = (token: string): number | null => {
  try {
    const { exp } = jwtDecode<JwtPayload>(token);
    return exp === undefined ? null : exp * 1_000 - Date.now();
  } catch {
    return null;
  }
};

export const createSseTokenProvider = (getAccessTokenSilently: GetAccessTokenSilently) => {
  let inFlightRefresh: Promise<string> | null = null;

  /**
   * Every subscription fails together, so the refresh has to be single-flight: the first caller
   * owns the request and the rest await it. Auth0 dedupes internally too, but its key omits
   * cacheMode, so an in-flight cached read can absorb a forced one and return the stale token.
   */
  const refresh = () => {
    inFlightRefresh ??= getAccessTokenSilently({ cacheMode: 'off' }).finally(() => {
      inFlightRefresh = null;
    });
    return inFlightRefresh;
  };

  return async ({ forceRefresh }: { forceRefresh: boolean }): Promise<string> => {
    if (forceRefresh) {
      return refresh();
    }

    const token = await getAccessTokenSilently();
    const remaining = msUntilExpiry(token);

    return remaining !== null && remaining < SSE_MIN_TOKEN_REMAINING_MS ? refresh() : token;
  };
};
